import { TypedDocumentNode, gql } from '@apollo/client';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { BodyM, BrandL, LabelM } from '@sorare/core/src/atoms/typography';
import { DateRange } from '@sorare/core/src/components/lobby/DateRange';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSimplifiedNav } from '@sorare/core/src/hooks/commonGame/useSimplifiedNav';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { isType } from '@sorare/core/src/lib/gql';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { LeagueEntryStateChip } from 'components/league/LeagueEntryStateChip';
import { LeagueSelect } from 'components/league/LeagueSelect';
import { LeagueTrckGroupSelect } from 'components/league/LeagueTrckGroupSelect';

import { FixtureCompetitionTabs } from '../FixtureCompetitionTabs';
import type { TrckGroupAside_so5Fixture } from './__generated__/index.graphql';

const Aside = styled(Vertical)`
  gap: calc(6 * var(--unit));
  position: relative;
  isolation: isolate;

  background-size: 100% 200px;
  background-image: linear-gradient(transparent, var(--c-black)),
    var(--header-background-url);
  background-repeat: no-repeat;

  @media ${desktopAndAbove} {
    gap: var(--triple-unit);
    background: initial;
    max-height: calc(100vh - var(--current-stack-height));
  }
`;
const Top = styled.div`
  position: relative;
`;
const Picture = styled.img`
  width: 100%;
  object-position: top;
  object-fit: contain;
  height: 188px;
`;
const FootballPicture = styled(Picture)`
  height: 260px;
`;
const Selects = styled(FullWidthContainer)`
  --selects-height: calc(8 * var(--unit));

  position: sticky;
  top: var(--current-stack-height);
  z-index: 1;
  isolation: isolate;

  min-height: var(--selects-height);
  display: flex;
  gap: var(--unit);
  align-items: center;
  overflow: hidden;

  @media ${desktopAndAbove} {
    top: 0;
    margin-top: calc((var(--selects-height) + var(--triple-unit)) * -1);
  }

  .scrolledFurtherThanImage & {
    ${regularMaterial}
  }
`;

type Props = {
  so5Fixture?: TrckGroupAside_so5Fixture;
  competition: string;
  trckGrp: string;
  trck: string;
  children: ReactNode;
  header?: ReactNode;
  navigateTo: (props: {
    competition: string;
    trckGrp: string;
    trck: string;
  }) => void;
  overrideDescription?: ReactNode;
  activeSkeletons?: 'all' | 'rules' | false;
  allowNavigatingWhenLocked?: boolean;
  hideFixtureCompetitionTabs?: boolean;
};

export const TrckGroupAside = ({
  so5Fixture,
  competition,
  trckGrp,
  trck,
  children,
  header,
  navigateTo,
  overrideDescription,
  activeSkeletons,
  allowNavigatingWhenLocked,
  hideFixtureCompetitionTabs,
}: Props) => {
  const { sport } = useSportContext();
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const [topRef, { height: topHeight }] = useMeasure<HTMLDivElement>();
  const [selectsRef, { height: selectsHeight }] = useMeasure<HTMLDivElement>();
  const ref = useRef<HTMLDivElement>(null);
  const [scrolledFurtherThanImage, setScrolledFurtherThanImage] =
    useState(false);

  useEffect(() => {
    const onScrollMobile = () => {
      if (isDesktopAndAbove) {
        return;
      }

      if (selectsHeight) {
        setScrolledFurtherThanImage(window.scrollY >= selectsHeight);
      }
    };
    window.addEventListener('scroll', onScrollMobile);

    return () => {
      window.removeEventListener('scroll', onScrollMobile);
    };
  }, [isDesktopAndAbove, selectsHeight]);

  const { showSimplifiedNav } = useSimplifiedNav(sport);

  const { competitionGroups: so5Leagues = [] } = so5Fixture || {};

  const so5League = so5Leagues.find(l => l.slug === competition);
  const { headerBackgroundUrl = '', pictureUrl = '' } = so5League || {};

  const so5LeagueTrackGroups = so5League?.so5LeagueTrackGroups || [];

  const selectedSo5LeagueTrackGroup = so5LeagueTrackGroups.find(
    l => l.slug === trckGrp
  );

  useEffect(() => {
    setScrolledFurtherThanImage(false);
  }, [selectedSo5LeagueTrackGroup]);

  const shouldDisplayTitle = sport === Sport.FOOTBALL;

  const shouldDisplayDescription =
    sport === Sport.FOOTBALL
      ? isType(so5League, 'So5League') && so5League.name === 'weekly'
      : true;

  const LeaguePicture = sport === Sport.FOOTBALL ? FootballPicture : Picture;
  return (
    <Aside
      ref={ref}
      onScroll={() => {
        if (isDesktopAndAbove && topHeight && ref.current) {
          setScrolledFurtherThanImage(ref.current.scrollTop >= topHeight);
        }
      }}
      className={classNames({ scrolledFurtherThanImage })}
      style={
        {
          '--header-background-url': `url(${activeSkeletons === 'all' ? '' : headerBackgroundUrl})`,
        } as CSSProperties
      }
    >
      <Selects ref={selectsRef}>
        <LeagueSelect
          selectedSo5LeaderboardGroupSlug={competition}
          so5LeaderboardGroupInterfaces={so5Leagues}
          rarity={selectedSo5LeagueTrackGroup?.mainRarityType || null}
          allowNavigatingWhenLocked={allowNavigatingWhenLocked}
          onClick={props => {
            navigateTo(props);
          }}
        />
        {!showSimplifiedNav && (
          <LeagueTrckGroupSelect
            useRarityName
            selectedSo5LeagueTrckGroupSlug={
              trckGrp || so5LeagueTrackGroups.at(0)?.slug || ''
            }
            so5LeagueTrckGroups={so5LeagueTrackGroups}
            allowNavigatingWhenLocked={allowNavigatingWhenLocked}
            onClick={props => {
              navigateTo({
                competition,
                ...props,
              });
            }}
          />
        )}
      </Selects>
      {!!isDesktopAndAbove && (
        <Skeleton loading={activeSkeletons === 'all'}>
          <Top ref={topRef}>
            {!!pictureUrl && <LeaguePicture src={pictureUrl} alt="" />}
          </Top>
        </Skeleton>
      )}
      <FullWidthContainer>
        <Vertical gap={3}>
          {!!so5Fixture && (
            <Horizontal gap={1}>
              <FontAwesomeIcon icon={faCalendar} width={12} />
              <LabelM bold>{so5Fixture.shortDisplayName}</LabelM>
              <svg
                width="2"
                height="2"
                viewBox="0 0 2 2"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="1" cy="1" r="1" fill="var(--c-nd-600)" />
              </svg>
              <LabelM color="var(--c-nd-600)">
                {!!so5Fixture && (
                  <DateRange
                    startDate={so5Fixture.startDate}
                    endDate={so5Fixture.endDate}
                  />
                )}
              </LabelM>
              <svg
                width="2"
                height="2"
                viewBox="0 0 2 2"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="1" cy="1" r="1" fill="var(--c-nd-600)" />
              </svg>
              {!!so5League && (
                <LabelM bold color="var(--c-yellow-400)">
                  <LeagueEntryStateChip so5LeaderboardGroup={so5League} />
                </LabelM>
              )}
            </Horizontal>
          )}

          <Skeleton loading={activeSkeletons === 'all'}>
            <Vertical gap={1}>
              {!!shouldDisplayTitle && (
                <BrandL>{so5League?.displayName}</BrandL>
              )}
              {!!shouldDisplayDescription &&
                (overrideDescription || (
                  <BodyM color="var(--c-nd-600)">
                    {so5League?.description}
                  </BodyM>
                ))}
            </Vertical>
          </Skeleton>
          {header}
          {!hideFixtureCompetitionTabs && (
            <FixtureCompetitionTabs
              trck={trck}
              so5LeagueTracks={
                selectedSo5LeagueTrackGroup?.so5LeagueTracks || []
              }
              onClick={({ trck: _trck }) => {
                navigateTo({
                  competition,
                  trckGrp,
                  trck: _trck,
                });
              }}
            />
          )}
          <Skeleton loading={!!activeSkeletons}>{children}</Skeleton>
        </Vertical>
      </FullWidthContainer>
    </Aside>
  );
};

TrckGroupAside.fragments = {
  so5Fixture: gql`
    fragment TrckGroupAside_so5Fixture on So5Fixture {
      slug
      shortDisplayName
      startDate
      endDate
      competitionGroups: so5LeaderboardGroups(groupType: COMPETITION) {
        slug
        displayName
        description
        headerBackgroundUrl
        pictureUrl
        so5LeagueTrackGroups {
          slug
          mainRarityType
          so5LineupsCount
          so5LeagueTracks {
            slug
            ...FixtureCompetitionTabs_so5LeagueTrack
          }
          ...LeagueTrckGroupSelect_so5LeagueTrackGroup
        }
        ...LeagueSelect_so5LeaderboardGroupInterface
        ...LeagueEntryStateChip_so5LeaderboardGroupInterface
      }
    }
    ${LeagueSelect.fragments.so5LeaderboardGroupInterface}
    ${LeagueTrckGroupSelect.fragments.so5LeagueTrackGroup}
    ${FixtureCompetitionTabs.fragments.so5LeagueTrack}
    ${LeagueEntryStateChip.fragments.so5LeaderboardGroupInterface}
  ` as TypedDocumentNode<TrckGroupAside_so5Fixture>,
};
